import * as React from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import { Bot, ChevronRight, Film, TriangleAlert, User } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Select } from "@/components/ui/select"
import type { Clip } from "@/components/clips/types"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

type AgentClipTodo = {
  id: number
  clip_id: number
  clip_title: string | null
  current_folder: string | null
  kind: string
  label: string
  state: string
}

type TodosProps = PageProps<{
  clips_awaiting_review: Clip[]
  agent_todos_for_clips: AgentClipTodo[]
}>

type TabKey = "you" | "agent"
type TabTone = "accent" | "signal"

const TAB_ACTIVE_CLASSES: Record<TabTone, string> = {
  accent: "border-accent text-accent-display",
  signal: "border-signal text-signal-display",
}

const TAB_COUNT_ACTIVE_CLASSES: Record<TabTone, string> = {
  accent: "bg-accent text-page",
  signal: "bg-signal text-ink-display",
}

const TAB_HOVER_BORDER_CLASSES: Record<TabTone, string> = {
  accent: "hover:border-accent-faded",
  signal: "hover:border-signal-faded",
}

function tabFromUrl(url: string): TabKey {
  const query = url.split("?")[1] ?? ""
  const tab = new URLSearchParams(query).get("tab")
  return tab === "agent" ? "agent" : "you"
}

export default function Todos() {
  const { props, url } = usePage<TodosProps>()
  const { clips_awaiting_review, agent_todos_for_clips } = props

  const userCount = clips_awaiting_review.length
  const agentCount = agent_todos_for_clips.length

  const [active, setActive] = React.useState<TabKey>(() => tabFromUrl(url))

  React.useEffect(() => {
    setActive(tabFromUrl(url))
  }, [url])

  const tabs: {
    key: TabKey
    label: string
    count: number
    icon: React.ComponentType<{ className?: string }>
    tone: TabTone
  }[] = [
    { key: "you", label: "For you", count: userCount, icon: User, tone: "accent" },
    { key: "agent", label: "For the agent", count: agentCount, icon: Bot, tone: "signal" },
  ]

  return (
    <>
      <Head title="To-dos">
        <meta name="description" content="Clip enrichments awaiting your review and the clips queued for the agent's next run." />
        <meta property="og:title" content="To-dos" />
        <meta property="og:description" content="Clip enrichments awaiting your review and the clips queued for the agent's next run." />
      </Head>
      <AppShell maxWidth="5xl">
        <header>
          <h1>To-dos</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Enrichments awaiting your review and the work queued for the agent on its next run.
          </p>
        </header>

        <div className="mt-6 md:hidden border-b border-hairline">
          <div className="py-2">
            <Select
              aria-label="To-dos section"
              value={active}
              onChange={(e) => setActive(e.target.value as TabKey)}
              className="w-full"
            >
              {tabs.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.label} ({tab.count})
                </option>
              ))}
            </Select>
          </div>
        </div>

        <nav className="mt-6 hidden md:flex items-end gap-6 border-b border-hairline">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = active === tab.key
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActive(tab.key)}
                className={cn(
                  "-mb-px inline-flex cursor-pointer items-center gap-2 border-b-2 px-1 py-3 text-sm",
                  isActive
                    ? cn("font-medium", TAB_ACTIVE_CLASSES[tab.tone])
                    : cn("border-transparent text-ink-body hover:text-ink-display", TAB_HOVER_BORDER_CLASSES[tab.tone]),
                )}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
                <span
                  className={cn(
                    "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold",
                    isActive ? TAB_COUNT_ACTIVE_CLASSES[tab.tone] : "bg-hairline text-ink-muted",
                  )}
                >
                  {tab.count}
                </span>
              </button>
            )
          })}
        </nav>

        {active === "you" && (
          <section className="mt-4">
            {clips_awaiting_review.length === 0 ? (
              <EmptyRow>Nothing waiting on you right now.</EmptyRow>
            ) : (
              <ul className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
                {clips_awaiting_review.map((clip) => (
                  <li key={clip.id}>
                    <Link
                      href={`/clips/${clip.id}`}
                      className="flex items-center gap-3 px-4 py-3 no-underline transition-colors hover:bg-surface"
                    >
                      {clip.primary_thumbnail_url ? (
                        <img
                          src={clip.primary_thumbnail_url}
                          alt=""
                          className="h-9 w-14 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <span className="flex h-9 w-14 shrink-0 items-center justify-center rounded bg-surface">
                          <Film className="h-4 w-4 text-ink-muted" />
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-ink-display">
                          {clip.title ?? clip.current_filename}
                        </div>
                        <div className="truncate text-xs text-ink-muted" title={clip.current_folder_path ?? ""}>
                          {clip.current_folder_path ?? "/"}
                        </div>
                      </div>
                      {clip.low_confidence_enrichment && (
                        <Badge tone="signal">
                          <TriangleAlert className="h-3 w-3" /> Low confidence
                        </Badge>
                      )}
                      <Badge tone="accent">Review enrichment</Badge>
                      <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {active === "agent" && (
          <section className="mt-4">
            {agent_todos_for_clips.length === 0 ? (
              <EmptyRow>No clip enrichment queued.</EmptyRow>
            ) : (
              <ul className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
                {agent_todos_for_clips.map((todo) => (
                  <li key={todo.id}>
                    <Link
                      href={`/clips/${todo.clip_id}`}
                      className="flex items-center gap-3 px-4 py-3 no-underline transition-colors hover:bg-surface"
                    >
                      <Film className="h-4 w-4 shrink-0 text-ink-muted" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-ink-display">
                          {todo.clip_title ?? `Clip #${todo.clip_id}`}
                        </div>
                        <div className="truncate text-xs text-ink-muted" title={todo.current_folder ?? ""}>
                          {todo.current_folder ?? "/"}
                        </div>
                      </div>
                      <Badge tone="signal">{todo.label}</Badge>
                      <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </AppShell>
    </>
  )
}

function EmptyRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-md border border-hairline bg-page">
      <p className="px-4 py-6 text-center text-sm text-ink-muted">{children}</p>
    </div>
  )
}
